import { useEffect } from "react";
import { X, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";

export const CartDrawer = () => {
    const { items, isOpen, setIsOpen, updateQuantity, removeItem, subtotal, count } = useCart();
    const navigate = useNavigate();

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => { document.body.style.overflow = ""; };
    }, [isOpen]);

    if (!isOpen) return null;

    const goCheckout = () => {
        setIsOpen(false);
        navigate("/checkout");
    };

    return (
        <div className="fixed inset-0 z-50" data-testid="cart-drawer-overlay">
            <div
                className="absolute inset-0"
                style={{ background: "rgba(59, 13, 33, 0.5)" }}
                onClick={() => setIsOpen(false)}
            />
            <aside
                className="absolute right-0 top-0 h-full w-full max-w-md bg-white flex flex-col fade-up"
                data-testid="cart-drawer"
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--sss-border)]">
                    <h3 className="font-serif-display text-2xl font-bold flex items-center gap-2">
                        <ShoppingBag size={20} className="text-[var(--sss-pink)]" />
                        Your Cart
                        <span className="text-sm font-sans text-[var(--sss-text-muted)] font-normal">({count})</span>
                    </h3>
                    <button
                        type="button"
                        onClick={() => setIsOpen(false)}
                        className="p-2 rounded-full hover:bg-[var(--sss-blush)]"
                        aria-label="Close cart"
                        data-testid="cart-close-button"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4">
                    {items.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center px-8 py-16">
                            <ShoppingBag size={48} className="text-[var(--sss-border)]" strokeWidth={1.2} />
                            <p className="mt-4 font-serif-display text-xl">Your cart is empty</p>
                            <p className="mt-2 text-sm text-[var(--sss-text-muted)]">Browse our handcrafted treats and add a little sweetness.</p>
                            <button
                                type="button"
                                onClick={() => { setIsOpen(false); navigate("/menu"); }}
                                className="btn-pink mt-6"
                                data-testid="cart-empty-shop-button"
                            >
                                Shop the Menu
                            </button>
                        </div>
                    ) : (
                        <ul className="divide-y divide-[var(--sss-border)]" data-testid="cart-items-list">
                            {items.map((it) => (
                                <li key={`${it.product_id}-${it.size}`} className="py-4 flex gap-3" data-testid={`cart-item-${it.product_id}`}>
                                    <div className="flex-1">
                                        <p className="font-serif-display text-lg leading-tight">{it.product_name}</p>
                                        <p className="text-xs text-[var(--sss-text-muted)]">{it.size}</p>
                                        <div className="mt-2 flex items-center gap-3">
                                            <div className="flex items-center border border-[var(--sss-border)] rounded-full">
                                                <button
                                                    type="button"
                                                    onClick={() => updateQuantity(it.product_id, it.size, it.quantity - 1)}
                                                    className="p-1.5 hover:bg-[var(--sss-blush)] rounded-full"
                                                    aria-label="Decrease"
                                                    data-testid={`cart-decrease-${it.product_id}`}
                                                >
                                                    <Minus size={14} />
                                                </button>
                                                <span className="px-2 text-sm font-medium">{it.quantity}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => updateQuantity(it.product_id, it.size, it.quantity + 1)}
                                                    className="p-1.5 hover:bg-[var(--sss-blush)] rounded-full"
                                                    aria-label="Increase"
                                                    data-testid={`cart-increase-${it.product_id}`}
                                                >
                                                    <Plus size={14} />
                                                </button>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeItem(it.product_id, it.size)}
                                                className="text-[var(--sss-text-muted)] hover:text-[var(--sss-pink)]"
                                                aria-label="Remove item"
                                                data-testid={`cart-remove-${it.product_id}`}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-serif-display font-semibold">${(it.unit_price * it.quantity).toFixed(2)}</p>
                                        <p className="text-xs text-[var(--sss-text-muted)]">${it.unit_price.toFixed(2)} ea</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {items.length > 0 && (
                    <div className="border-t border-[var(--sss-border)] px-5 py-5 bg-[var(--sss-blush)]">
                        <div className="flex items-center justify-between mb-4">
                            <span className="font-medium">Subtotal</span>
                            <span className="font-serif-display text-2xl font-bold" data-testid="cart-subtotal">
                                ${subtotal.toFixed(2)}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={goCheckout}
                            className="btn-pink w-full justify-center"
                            data-testid="cart-checkout-button"
                        >
                            Continue to Checkout
                        </button>
                        <p className="text-xs text-center text-[var(--sss-text-muted)] mt-3">
                            Pay via PayPal, Cash App, Apple Pay, or Cash on confirmation.
                        </p>
                    </div>
                )}
            </aside>
        </div>
    );
};
